import { AwesomeEvent, ComponentAPI } from '@philips-emr/tws-core';
import Changeable, { ChangeEventListener } from '@philips-emr/tws-core/lib/component/types/changeable';
import Focusable, { FocusEventListener } from '@philips-emr/tws-core/lib/component/types/focusable';
import { isObject } from '@philips-emr/tws-utils';

interface Parameters {
  [key: string]: any;
}

interface SingleSelection {
  [key: string]: any;
}

interface GroupedSingleSelection {
  [key: string]: SingleSelection;
}

type MultiSelection = SingleSelection[];
interface GroupedMultiSelection {
  [key: string]: MultiSelection;
}

export type FinderValue =
  | SingleSelection
  | GroupedSingleSelection
  | MultiSelection
  | GroupedMultiSelection
  | null
  | undefined;

function isValidValue(value: FinderValue, grouped: boolean, multiselect: boolean): boolean {
  if (!grouped && !multiselect) {
    return isObject(value);
  }

  if (!grouped && multiselect) {
    return Array.isArray(value);
  }

  if (grouped) {
    if (!isObject(value)) {
      return false;
    }

    if (multiselect) {
      const castedValue = value as GroupedMultiSelection;
      return Object.values(castedValue).every(value => Array.isArray(value));
    }

    return true;
  }

  return false;
}

class FinderAPI extends ComponentAPI implements Changeable<FinderValue>, Focusable {
  /** @event */
  @AwesomeEvent
  onChange = new ChangeEventListener<FinderValue>();

  /** @event */
  @AwesomeEvent
  onBlur = new FocusEventListener();

  /** @event */
  @AwesomeEvent
  onFocus = new FocusEventListener();

  getChangeRecordExpression(): string {
    return this.state.changeRecordExpression;
  }

  setChangeRecordExpression(changeRecordExpression: string): void {
    this.setState({ changeRecordExpression });
  }

  getDescriptionKey(): string {
    return this.state.descriptionKey;
  }

  setDescriptionKey(descriptionKey: string): void {
    this.setState({ descriptionKey });
  }

  isEnabled(): boolean {
    return this.state.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.setState({ enabled });
  }

  isGrouped(): boolean {
    return this.state.grouped;
  }

  getMinSearchLength(): number {
    return this.state.minSearchLength;
  }

  setMinSearchLength(minSearchLength: number): void {
    this.setState({ minSearchLength });
  }

  isMultiSelect(): boolean {
    return this.state.multiselect;
  }

  getParameters(): Parameters {
    return this.state.parameters;
  }

  setParameters(parameters: Parameters): void {
    this.setState({ parameters });
  }

  getPlaceholder(): string {
    return this.state.placeholder;
  }

  setPlaceholder(placeholder: string): void {
    this.setState({ placeholder });
  }

  getPrimaryKey(): string {
    return this.state.primaryKey;
  }

  setPrimaryKey(primaryKey: string): void {
    this.setState({ primaryKey });
  }

  getQueryKey(): string {
    return this.state.queryKey;
  }

  setQueryKey(queryKey: string): void {
    this.setState({ queryKey });
  }

  isReadOnly(): boolean {
    return this.state.readOnly;
  }

  setReadOnly(readOnly: boolean): void {
    this.setState({ readOnly });
  }

  getValue(): FinderValue {
    return this.state.value;
  }

  setValue(value: FinderValue): void {
    if (isValidValue(value, this.isGrouped(), this.isMultiSelect())) {
      this.setState({ value });
    }

    throw new Error('The informed value is not valid. Please check the FinderValue type.');
  }
}

export default FinderAPI;
